/**
 * Canonical account types, paid products, seat tiers, and feature gates.
 * Single source of truth for REChecklist and DreamHome Journey checkout.
 *
 * Product names (launch): Agent Standard · Broker Office · Enterprise
 */

export const PRODUCT_IDS = {
  CLIENT: "client",
  AGENT_STANDARD: "agent_standard",
  BROKER_OFFICE: "broker_office",
  ENTERPRISE: "enterprise",
  BETA: "beta",
  ADMIN: "admin",
};

/** Login roles and how they relate to billing. */
export const ACCOUNT_TYPES = [
  { id: "buyer", label: "Buyer", kind: "client", billing: "included", defaultProduct: PRODUCT_IDS.CLIENT },
  { id: "seller", label: "Seller", kind: "client", billing: "included", defaultProduct: PRODUCT_IDS.CLIENT },
  {
    id: "agent",
    label: "Agent",
    kind: "professional",
    billing: "subscription",
    defaultProduct: PRODUCT_IDS.AGENT_STANDARD,
  },
  {
    id: "broker",
    label: "Broker",
    kind: "professional",
    billing: "subscription",
    defaultProduct: PRODUCT_IDS.BROKER_OFFICE,
  },
  {
    id: "corporate",
    label: "Enterprise",
    kind: "professional",
    billing: "subscription",
    defaultProduct: PRODUCT_IDS.ENTERPRISE,
  },
  { id: "admin", label: "Admin", kind: "internal", billing: "none", defaultProduct: PRODUCT_IDS.ADMIN },
];

export const AGENT_STANDARD_PLAN = {
  id: "agent_monthly",
  productId: PRODUCT_IDS.AGENT_STANDARD,
  sku: "agent",
  tier: "",
  name: "Agent Standard",
  role: "agent",
  monthly: 39.99,
  annual: 290,
  currency: "USD",
};

export const BROKER_SEAT_TIERS = [
  {
    id: "10",
    label: "Up to 10 agents",
    shortLabel: "Broker Office · 10 seats",
    maxAgents: 10,
    monthly: 149,
    annual: 1490,
    planId: "broker_10",
    productId: PRODUCT_IDS.BROKER_OFFICE,
  },
  {
    id: "20",
    label: "Up to 20 agents",
    shortLabel: "Broker Office · 20 seats",
    maxAgents: 20,
    monthly: 199,
    annual: 1990,
    planId: "broker_20",
    productId: PRODUCT_IDS.BROKER_OFFICE,
  },
  {
    id: "30",
    label: "Up to 30 agents",
    shortLabel: "Broker Office · 30 seats",
    maxAgents: 30,
    monthly: 249,
    annual: 2490,
    planId: "broker_30",
    productId: PRODUCT_IDS.BROKER_OFFICE,
  },
  {
    id: "40",
    label: "Up to 40 agents",
    shortLabel: "Broker Office · 40 seats",
    maxAgents: 40,
    monthly: 299,
    annual: 2990,
    planId: "broker_40",
    productId: PRODUCT_IDS.BROKER_OFFICE,
  },
  {
    id: "50",
    label: "Up to 50 agents",
    shortLabel: "Broker Office · 50 seats",
    maxAgents: 50,
    monthly: 349,
    annual: 3490,
    planId: "broker_50",
    productId: PRODUCT_IDS.BROKER_OFFICE,
  },
];

export const ENTERPRISE_PLAN = {
  id: "corporate",
  productId: PRODUCT_IDS.ENTERPRISE,
  sku: "corporate",
  tier: "",
  name: "Enterprise",
  role: "corporate",
  monthly: 599,
  annual: 5990,
  currency: "USD",
};

export const BETA_PLAN = {
  id: "beta",
  productId: PRODUCT_IDS.BETA,
  name: "Beta (comped)",
  role: "any",
  monthly: 0,
  annual: 0,
  currency: "USD",
};

const BROKER_OFFICE_FEATURES = [
  "broker_desk",
  "agent_management",
  "agent_controls",
  "client_manager",
  "client_messaging",
  "office_messaging",
  "files",
  "documents",
  "calendar",
  "hub",
  "prospects",
  "reports",
  "archive",
  "retention",
  "broker_scope",
];

const AGENT_STANDARD_FEATURES = [
  "agent_desk",
  "client_manager",
  "client_messaging",
  "office_messaging",
  "files",
  "documents",
  "calendar",
  "hub",
  "prospects",
  "reports",
  "client_invite",
];

/** Paid products and the desk capabilities each unlocks. */
export const PRODUCTS = {
  [PRODUCT_IDS.CLIENT]: {
    id: PRODUCT_IDS.CLIENT,
    name: "Client Journey",
    roles: ["buyer", "seller"],
    planIds: [],
    features: ["buyer_journey", "seller_journey", "client_messaging", "client_export"],
  },
  [PRODUCT_IDS.AGENT_STANDARD]: {
    id: PRODUCT_IDS.AGENT_STANDARD,
    name: "Agent Standard",
    roles: ["agent"],
    planIds: [AGENT_STANDARD_PLAN.id],
    features: AGENT_STANDARD_FEATURES,
  },
  [PRODUCT_IDS.BROKER_OFFICE]: {
    id: PRODUCT_IDS.BROKER_OFFICE,
    name: "Broker Office",
    roles: ["broker"],
    planIds: BROKER_SEAT_TIERS.map((row) => row.planId),
    features: BROKER_OFFICE_FEATURES,
  },
  [PRODUCT_IDS.ENTERPRISE]: {
    id: PRODUCT_IDS.ENTERPRISE,
    name: "Enterprise",
    roles: ["broker", "corporate"],
    planIds: [ENTERPRISE_PLAN.id],
    features: [...BROKER_OFFICE_FEATURES, "enterprise_reporting", "multi_office", "enterprise_integrations"],
  },
  [PRODUCT_IDS.BETA]: {
    id: PRODUCT_IDS.BETA,
    name: "Beta (comped)",
    roles: ["agent", "broker"],
    planIds: [BETA_PLAN.id],
    features: [...AGENT_STANDARD_FEATURES, ...BROKER_OFFICE_FEATURES],
  },
  [PRODUCT_IDS.ADMIN]: {
    id: PRODUCT_IDS.ADMIN,
    name: "Admin",
    roles: ["admin"],
    planIds: [],
    features: ["*"],
  },
};

/** Desk surfaces each role may use when properly entitled. */
export const ROLE_DESKS = {
  buyer: { desk: "buyer", path: "/start", product: PRODUCT_IDS.CLIENT },
  seller: { desk: "seller", path: "/seller/home", product: PRODUCT_IDS.CLIENT },
  agent: { desk: "agent", path: "/agent/dashboard", product: PRODUCT_IDS.AGENT_STANDARD },
  broker: { desk: "broker", path: "/broker/dashboard", product: PRODUCT_IDS.BROKER_OFFICE },
  corporate: { desk: "broker", path: "/broker/dashboard", product: PRODUCT_IDS.ENTERPRISE },
  admin: { desk: "admin", path: "/admin", product: PRODUCT_IDS.ADMIN },
};

export function accountTypeById(id) {
  return ACCOUNT_TYPES.find((row) => row.id === id) || null;
}

export function productById(id) {
  return PRODUCTS[id] || null;
}

export function seatTierById(id) {
  return BROKER_SEAT_TIERS.find((row) => row.id === String(id)) || null;
}

export function planByCatalogId(planId) {
  const id = String(planId || "");
  if (id === AGENT_STANDARD_PLAN.id) return AGENT_STANDARD_PLAN;
  if (id === ENTERPRISE_PLAN.id) return ENTERPRISE_PLAN;
  if (id === BETA_PLAN.id) return BETA_PLAN;
  const seat = BROKER_SEAT_TIERS.find((row) => row.planId === id);
  if (seat) {
    return {
      id: seat.planId,
      productId: PRODUCT_IDS.BROKER_OFFICE,
      sku: "broker",
      tier: seat.id,
      name: seat.shortLabel,
      role: "broker",
      monthly: seat.monthly,
      annual: seat.annual,
      currency: "USD",
      maxAgents: seat.maxAgents,
    };
  }
  return null;
}

/** Checkout SKUs consumed by PayPal gateway and Journey /pay. */
export function billingSkus() {
  const rows = [
    {
      id: AGENT_STANDARD_PLAN.id,
      sku: AGENT_STANDARD_PLAN.sku,
      tier: "",
      label: AGENT_STANDARD_PLAN.name,
      amount: AGENT_STANDARD_PLAN.monthly.toFixed(2),
      amountAnnual: AGENT_STANDARD_PLAN.annual.toFixed(2),
      currency: AGENT_STANDARD_PLAN.currency,
    },
    ...BROKER_SEAT_TIERS.map((seat) => ({
      id: seat.planId,
      sku: "broker",
      tier: seat.id,
      label: seat.shortLabel,
      amount: seat.monthly.toFixed(2),
      amountAnnual: seat.annual.toFixed(2),
      currency: "USD",
    })),
    {
      id: ENTERPRISE_PLAN.id,
      sku: ENTERPRISE_PLAN.sku,
      tier: "",
      label: ENTERPRISE_PLAN.name,
      amount: ENTERPRISE_PLAN.monthly.toFixed(2),
      amountAnnual: ENTERPRISE_PLAN.annual.toFixed(2),
      currency: ENTERPRISE_PLAN.currency,
    },
  ];
  return rows;
}

/** Admin Account Management plan picker — aligned with checkout catalog. */
export function adminPlanOptions() {
  return [
    {
      id: AGENT_STANDARD_PLAN.id,
      label: AGENT_STANDARD_PLAN.name,
      amount: AGENT_STANDARD_PLAN.monthly,
      interval: "month",
      role: "agent",
      productId: PRODUCT_IDS.AGENT_STANDARD,
      seats: 0,
    },
    ...BROKER_SEAT_TIERS.map((seat) => ({
      id: seat.planId,
      label: seat.shortLabel,
      amount: seat.monthly,
      interval: "month",
      role: "broker",
      productId: PRODUCT_IDS.BROKER_OFFICE,
      seats: seat.maxAgents,
    })),
    {
      id: ENTERPRISE_PLAN.id,
      label: ENTERPRISE_PLAN.name,
      amount: ENTERPRISE_PLAN.monthly,
      interval: "month",
      role: "corporate",
      productId: PRODUCT_IDS.ENTERPRISE,
      seats: 0,
    },
    {
      id: BETA_PLAN.id,
      label: BETA_PLAN.name,
      amount: 0,
      interval: "month",
      role: "any",
      productId: PRODUCT_IDS.BETA,
      seats: 0,
    },
  ];
}

export function seatTierMap() {
  return Object.fromEntries(
    BROKER_SEAT_TIERS.map((row) => [row.id, { id: row.id, label: row.label, max: row.maxAgents }])
  );
}

export function seatLabel(tierId) {
  return seatTierById(tierId)?.label || "No seat plan yet";
}

export function seatMax(tierId) {
  return seatTierById(tierId)?.maxAgents ?? Number.POSITIVE_INFINITY;
}

export function defaultSeatTierId() {
  return BROKER_SEAT_TIERS[0]?.id || "10";
}

export function defaultBrokerPlanId() {
  return BROKER_SEAT_TIERS[0]?.planId || "broker_10";
}

export function brokerPlanIdForTier(tierId) {
  return seatTierById(tierId)?.planId || defaultBrokerPlanId();
}

export function tierIdForBrokerPlan(planId) {
  const seat = BROKER_SEAT_TIERS.find((row) => row.planId === planId);
  return seat?.id || defaultSeatTierId();
}

export function allowedProductsForRole(role) {
  return Object.values(PRODUCTS).filter((product) => product.roles.includes(role));
}

export function normalizePlanId(planId, seatTier) {
  const raw = String(planId || "").trim();
  if (raw === "broker_tier" && seatTier) return brokerPlanIdForTier(seatTier);
  if (raw.startsWith("broker_")) return raw;
  if (raw === "enterprise") return ENTERPRISE_PLAN.id;
  if (raw === AGENT_STANDARD_PLAN.id || raw === "agent") return AGENT_STANDARD_PLAN.id;
  if (raw === ENTERPRISE_PLAN.id || raw === "corporate") return ENTERPRISE_PLAN.id;
  if (raw === BETA_PLAN.id) return BETA_PLAN.id;
  if (seatTier && !raw) return brokerPlanIdForTier(seatTier);
  return raw || null;
}

/**
 * Resolve the paid product for a session or user record.
 * @param {{ role?: string, plan?: string|null, seat_tier?: string|null, betaTester?: boolean, plan_status?: string|null }} session
 */
export function resolveSessionProduct(session) {
  if (!session?.role) return null;
  const role = session.role;

  if (role === "admin") return PRODUCTS[PRODUCT_IDS.ADMIN];
  if (role === "buyer" || role === "seller") return PRODUCTS[PRODUCT_IDS.CLIENT];

  const planId = normalizePlanId(session.plan, session.seat_tier);
  const comped =
    session.betaTester ||
    planId === BETA_PLAN.id ||
    session.plan_status === "beta" ||
    session.plan === "beta";

  if (comped) return PRODUCTS[PRODUCT_IDS.BETA];

  if (planId === ENTERPRISE_PLAN.id || role === "corporate") {
    return PRODUCTS[PRODUCT_IDS.ENTERPRISE];
  }

  if (role === "agent") {
    if (!planId || planId === AGENT_STANDARD_PLAN.id) return PRODUCTS[PRODUCT_IDS.AGENT_STANDARD];
    if (planId.startsWith("broker_")) return null;
    return PRODUCTS[PRODUCT_IDS.AGENT_STANDARD];
  }

  if (role === "broker") {
    if (planId === ENTERPRISE_PLAN.id) return PRODUCTS[PRODUCT_IDS.ENTERPRISE];
    if (planId?.startsWith("broker_") || session.seat_tier) {
      return PRODUCTS[PRODUCT_IDS.BROKER_OFFICE];
    }
    return PRODUCTS[PRODUCT_IDS.BROKER_OFFICE];
  }

  return null;
}

export function productLabel(session) {
  const product = resolveSessionProduct(session);
  if (!product) return "Unknown plan";
  if (product.id === PRODUCT_IDS.BROKER_OFFICE && session?.seat_tier) {
    const seat = seatTierById(session.seat_tier);
    return seat ? `${product.name} · ${seat.label}` : product.name;
  }
  return product.name;
}

export function deskForRole(role) {
  return ROLE_DESKS[role]?.desk || null;
}

export function defaultPathForRole(role) {
  return ROLE_DESKS[role]?.path || "/";
}

export function canAccessDesk(session, desk) {
  if (!session?.role) return desk === "buyer" || desk === "seller";
  if (session.role === "admin") return true;
  const product = resolveSessionProduct(session);
  if (!product) return false;

  if (desk === "buyer") {
    return session.role === "buyer" || session.role === "admin";
  }
  if (desk === "seller") {
    return session.role === "seller" || session.role === "admin";
  }
  if (desk === "agent") {
    if (session.role !== "agent" && session.role !== "admin") return false;
    return product.features.includes("*") || product.features.includes("agent_desk");
  }
  if (desk === "broker") {
    if (!["broker", "corporate", "admin"].includes(session.role)) return false;
    return (
      product.features.includes("*") ||
      product.features.includes("broker_desk") ||
      product.features.includes("enterprise_reporting")
    );
  }
  if (desk === "admin") return session.role === "admin";
  return false;
}

export function canAccessFeature(session, featureId) {
  if (!session?.role) return false;
  const product = resolveSessionProduct(session);
  if (!product) return false;
  if (product.features.includes("*")) return true;
  return product.features.includes(featureId);
}

export function assertDeskAccess(session, desk) {
  if (!canAccessDesk(session, desk)) {
    const product = resolveSessionProduct(session);
    throw new Error(
      `${product?.name || "This account"} does not include the ${desk} desk. Choose the platform that matches your subscription.`
    );
  }
}

export function plansForRole(role) {
  return adminPlanOptions().filter((plan) => plan.role === role || plan.role === "any");
}
